import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import handCursor from 'assets/hand-cursor.png';

import styles from './toolbar.module.css';
import { IconButton } from 'components/Icon-button/icon-button';
import {
	DEFAULT_CURSOR,
	ToolbarContext,
} from 'context/toolbar-context/toolbar-context';
import { TOOLS } from 'constants/tools';
import { ActiveLayerContext } from 'context/active-layer-context/active-layer-context';
import { CanvasContext } from 'context/canvas-context/canvas-context';
import { TextEditorContext } from 'context/text-editor-context/text-editor-context';
import { DEFAULT_FONT_SIZE } from 'constants/font';
import { useSaveText } from 'hooks/use-save-text';
import { Menu, MenuItem } from 'components/menu';
import { zoomPercentageToScale } from 'utils/base';
import { getTransformedPoint } from 'utils/geometry';

type Props = {};

const CURSORS = {
	[TOOLS.HAND]: `-webkit-image-set(url("${handCursor}") 2x), auto`,
};

/**
 * Toolbar Component
 */
export const Toolbar: React.FC<Props> = () => {
	const { tool, setTool, setCursor, zoomPercentage, setZoomPercentage } =
		useContext(ToolbarContext);
	const { activeLayer, setActiveLayer } = useContext(ActiveLayerContext);
	const { renderingManager, renderer } = useContext(CanvasContext);
	const {
		editable,
		setTextAlign,
		setText,
		setEditable,
		setFontSize,
		setBold,
		setItalic,
	} = useContext(TextEditorContext);
	const saveText = useSaveText();

	const setCurrentTool = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			setTool(event.currentTarget.value);

			if (CURSORS[event.currentTarget.value]) {
				setCursor(CURSORS[event.currentTarget.value]);
			} else {
				setCursor(DEFAULT_CURSOR);
			}

			if (activeLayer) {
				if (editable) {
					saveText();
					setText('');
					setFontSize(DEFAULT_FONT_SIZE);
					setBold(false);
					setItalic(false);
					setTextAlign('left');
					setEditable(false);
				}

				activeLayer.setActive(false);
				setActiveLayer(null);
				renderingManager?.reDraw();
			}
		},
		[
			activeLayer,
			editable,
			renderingManager,
			saveText,
			setActiveLayer,
			setBold,
			setCursor,
			setEditable,
			setFontSize,
			setItalic,
			setText,
			setTextAlign,
			setTool,
		]
	);

	const zooming = (step: number, edge: number) => {
		if (renderer && renderingManager) {
			const { scaleX } = renderer.getTransform();

			const nextZoomPercentage =
				step > 0
					? Math.min(zoomPercentage + step, edge)
					: Math.max(zoomPercentage + step, edge);

			const scale = zoomPercentageToScale(nextZoomPercentage) / scaleX;

			let currentTransformedPosition = getTransformedPoint(
				window.innerWidth / 2,
				window.innerHeight / 2,
				renderer.getTransform()
			);

			if (activeLayer) {
				const options = activeLayer.getOptions();

				currentTransformedPosition = {
					x: options.x + (options.w * scale) / 2,
					y: options.y + (options.h * scale) / 2,
				};
			}

			if (
				step > 0
					? nextZoomPercentage <= edge
					: nextZoomPercentage >= edge
			) {
				renderer.translate(
					currentTransformedPosition.x,
					currentTransformedPosition.y
				);
				renderer.scale(scale, scale);
				renderer.translate(
					-currentTransformedPosition.x,
					-currentTransformedPosition.y
				);
				renderingManager?.reDraw({ force: true });
				setZoomPercentage(nextZoomPercentage);
			}
		}
	};

	const zoomIn = () => {
		zooming(10, 200);
	};

	const zoomOut = () => {
		zooming(-10, 10);
	};

	return (
		<>
			<div className={styles.bottomToolbarContainer}>
				<Menu theme="light">
					<MenuItem>
						<IconButton
							fill
							onClick={zoomOut}
							icon="minus"
							size="medium"
							color="main-dark"
							value={TOOLS.ZOOM}
						/>
					</MenuItem>
					<MenuItem>{zoomPercentage}%</MenuItem>
					<MenuItem>
						<IconButton
							fill
							onClick={zoomIn}
							icon="plus"
							size="medium"
							color="main-dark"
							value={TOOLS.ZOOM}
						/>
					</MenuItem>
				</Menu>
			</div>
			<div className={styles.leftToolbarContainer}>
				<ul className={styles.list}>
					<li
						className={classNames(styles.item, {
							[styles.selected]: tool === TOOLS.HAND,
						})}
					>
						<IconButton
							fill
							onClick={setCurrentTool}
							icon="hand"
							size="medium"
							color="main-white"
							value={TOOLS.HAND}
						/>
					</li>
					<li
						className={classNames(styles.item, {
							[styles.selected]: [
								TOOLS.RESIZER,
								TOOLS.SELECT,
							].includes(tool),
						})}
					>
						<IconButton
							fill
							onClick={setCurrentTool}
							icon="cursor"
							size="medium"
							color="main-white"
							value={TOOLS.SELECT}
						/>
					</li>
					<li
						className={classNames(styles.item, {
							[styles.selected]: tool === TOOLS.STICKER,
						})}
					>
						<IconButton
							fill
							onClick={setCurrentTool}
							icon="sticker"
							size="medium"
							color="main-white"
							value={TOOLS.STICKER}
						/>
					</li>
					<li
						className={classNames(styles.item, {
							[styles.selected]: tool === TOOLS.CARD,
						})}
					>
						<IconButton
							fill
							onClick={setCurrentTool}
							icon="card"
							size="medium"
							color="main-white"
							value={TOOLS.CARD}
						/>
					</li>
				</ul>
			</div>
		</>
	);
};
